/* ============================================================
   Modal "Schedule template" — /workspace-v3.9

   Susun atur mengikut penyunting availability Cal.com:
     KIRI  · satu tetapan menonjol di atas (toggle + penerangan),
             kemudian sekeping kad berisi senarai baris tetapan,
             dengan zon waktu di kakinya.
     KANAN · kalendar bulan menunjukkan hari mana video jatuh, dan
             di sebelahnya senarai waktu bagi hari yang dipilih.

   Sebabnya bukan sekadar rupa: pengguna kita menyusun 30–60 video
   merentas beberapa hari. Senarai rata tidak menjawab soalan pertama
   yang dia tanya — "hari apa yang penuh?" — sedangkan kalendar
   menjawabnya sebelum dia sempat menatal.

   Modal ini tidak memegang produk. Ia dibuka daripada bar tab tanpa
   perlu menandakan apa-apa kad; produk dipilih kemudian, semasa Run.
   ============================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Globe, Copy, Trash2, Plus, AlertTriangle, Check, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';

import { Badge, Btn, EmptyState, IconBtn } from '../design/ui';
import PilihWaktuV39 from './PilihWaktuV39';
import PilihV39 from './PilihV39';
import {
    INTERVALS, LANGKAH_MINIT,
    jamKeMinit, laraskanJam, isiSelang, cadangWaktuBaharu,
    namaAuto, jadualBaharu, salinJadual,
} from './scheduleV39';
/* Pembantu susun-semula yang SAMA seperti senarai agent — kaedah tunggal
   bagi setiap peranti: tetikus dapat tarik-lepas, sentuh dapat anak panah.
   Tulen dan tiada kaitan Firestore, jadi ia dipakai di sini terus atas
   array `senarai` (susunannya disimpan verbatim ke browser + server). */
import { alihItem, sisipItem, bolehTarikTetikus } from '../utils/susunAgent';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const HARI_SATU_MS = 86400000;

/* ⭐ PROP `t` — dua bahasa TANPA mengubah rupa lama.

   Modal ini DIKONGSI: ia dirender oleh halaman v3.9 (WorkspaceV39View)
   DAN oleh workspace rupa lama (WorkspaceView). Kalau ia memanggil
   useT() terus, pelanggan yang masih pada rupa lama — iaitu semua
   orang sehingga v3.9 dilancarkan — tiba-tiba nampak modal ini dalam
   English, kerana bahasa lalai app ialah English.

   Jadi corak yang sama seperti DashboardSections.jsx: halaman v3.9
   menghantar fungsi terjemahannya masuk; pemanggil yang tidak
   menghantar apa-apa kekal dengan ayat Melayu di bawah, huruf demi
   huruf. Bila v3.9 menjadi satu-satunya rupa, prop ini boleh dibuang
   dan useT() dipanggil terus. */
const ambilTeks = (t) => (kunci, lalai, vars) => (t ? t(kunci, vars) : lalai);

/* ---------- Bahagian kecil ------------------------------------------ */

/* Alat pengisi senarai waktu. Ia BUKAN mod — hasilnya ditulis terus ke
   dalam senarai, dan selepas itu setiap waktu berdiri sendiri: boleh
   ditanggalkan, boleh ditambah. Ini yang membolehkan "setiap 30 minit dari
   8 pagi ke 11 malam" dibina dengan satu klik tanpa memaksa pengguna
   menanda 31 kotak — dan tanpa mengunci dia kepada peraturan itu. */
const KUNCI_ISI = 'sesaat_jadual_isi_v1';
const ISI_LALAI = { mula: '08:00', henti: '23:00', selang: '30' };

/* Kedudukan terakhir alat "Isi automatik" diingat PER BROWSER.

   Ia BUKAN tetapan template — `slot` kekal satu-satunya sumber waktu
   (lihat scheduleV39.js), dan tiada apa-apa di sini yang masuk ke dalam
   jadual yang disimpan. Yang diingat hanyalah kedudukan alat itu.

   Sebabnya: alat ini lahir semula setiap kali modal dibuka (modal
   dirender bersyarat, jadi menutupnya melenyapkan seluruh keadaannya),
   dan ia kembali kepada "30 minit, 08:00 → 23:00". Pengguna yang baru
   sahaja membina jadual setiap 1 jam membacanya sebagai JADUALNYA yang
   patah balik ke 30 minit — aduan user 2026-08-24. Senarai waktu di
   bawah memang betul sepanjang masa; hanya alat ini yang terlupa.

   ⛔ Ditulis HANYA apabila pengguna menyentuh kawalan. Jangan sekali-kali
      menyimpannya dari useEffect — effect begitu turut berjalan semasa
      mount, jadi setiap browser akan menyimpan nilai lalai walaupun
      penggunanya tidak pernah memilih apa-apa, dan menukar nilai lalai
      selepas itu langsung tidak berkesan (perangkap yang sama seperti
      `sesaat_products_paparan_v2` pada halaman Products). */
const bacaIsi = () => {
    try {
        const simpan = JSON.parse(localStorage.getItem(KUNCI_ISI) || '{}');
        /* Waktu dilaraskan semula ke roda 5-minit TikTok. `laraskanJam`
           memang jaring untuk nilai yang MASUK dari luar seperti ini —
           tetapi bentuknya disemak dahulu, kerana teks rosak jatuh
           menjadi 00:00 di dalamnya, bukan kepada nilai lalai. */
        const waktu = (v, ganti) => (/^[0-9]{2}:[0-9]{2}$/.test(v) ? laraskanJam(v) : ganti);
        return {
            mula: waktu(simpan.mula, ISI_LALAI.mula),
            henti: waktu(simpan.henti, ISI_LALAI.henti),
            /* Selang disemak lawan senarai sebenar, bukan sekadar "ada
               nilai" — nombor di luar senarai memapar kotak KOSONG, dan
               pengguna tidak tahu apa yang akan berlaku bila dia tekan
               Isi. */
            selang: INTERVALS.some((i) => i.value === simpan.selang) ? simpan.selang : ISI_LALAI.selang,
        };
    } catch { return ISI_LALAI; }   /* mod privasi / JSON rosak */
};

function IsiAutomatik({ onIsi, onKosong, adaIsi, T }) {
    const [isi, setIsi] = useState(bacaIsi);
    const { mula, henti, selang } = isi;

    /* Satu laluan tulis untuk ketiga-tiga kawalan — kalau setiap satu
       menyimpan sendiri, satu yang tertinggal akan patah balik seorang
       diri, dan itu lebih mengelirukan daripada semuanya patah balik. */
    const ubahIsi = (patch) => {
        const baru = { ...isi, ...patch };
        setIsi(baru);
        try { localStorage.setItem(KUNCI_ISI, JSON.stringify(baru)); } catch { /* mod privasi */ }
    };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            padding: '9px 10px', borderRadius: 'var(--s-r-md, 10px)',
            border: '1px dashed var(--s-line-strong)', background: 'var(--s-fill)',
        }}>
            <span style={{ fontSize: 12, color: 'var(--s-ink-2)' }}>{T('workspace.schedule.fillEvery', 'Setiap')}</span>
            <PilihV39
                ariaLabel={T('workspace.schedule.fillIntervalAria', 'Jarak antara waktu')}
                nilai={selang}
                onPilih={(v) => ubahIsi({ selang: v })}
                pilihan={INTERVALS.map((i) => ({ id: i.value, label: i.label }))}
                tinggi={28}
                saizTeks={13}
                lebar={112}
                lebarMenu={140}
            />
            <span style={{ fontSize: 12, color: 'var(--s-ink-2)' }}>{T('workspace.schedule.fillFrom', 'dari')}</span>
            <PilihWaktuV39 value={mula} onChange={(v) => ubahIsi({ mula: v })} label={T('workspace.schedule.startTimeAria', 'Waktu mula')} tinggi={28} />
            <span style={{ fontSize: 12, color: 'var(--s-ink-2)' }}>{T('workspace.schedule.fillTo', 'hingga')}</span>
            <PilihWaktuV39 value={henti} onChange={(v) => ubahIsi({ henti: v })} label={T('workspace.schedule.endTimeAria', 'Waktu berhenti')} tinggi={28} />
            <Btn size="sm" onClick={() => onIsi(mula, henti, selang)}>{T('workspace.schedule.fillBtn', 'Isi')}</Btn>
            {adaIsi && <Btn size="sm" variant="quiet" onClick={onKosong}>{T('workspace.schedule.clearBtn', 'Kosongkan')}</Btn>}
        </div>
    );
}

/* Menambah SATU waktu ke dalam senarai.

   Sebelum ini mod itu hanya menerima sepuluh waktu siap-pilih dalam
   `SLOT_PILIHAN` (06:00, 08:00, … setiap dua jam). Hadnya bukan pada enjin —
   `jadual.slot` memang array "HH:MM" biasa dan seluruh laluan hiliran
   (`slotHari`, `letakMasa`, `masaSeterusnya`) sudah menerima apa-apa waktu.
   Yang mengunci pengguna hanyalah baris pemilih itu. */
function TambahWaktuTemplate({ sudahAda = [], onTambah, T }) {
    const [buka, setBuka] = useState(false);
    const [jam, setJam] = useState('09:00');
    const [nota, setNota] = useState('');

    const tutup = () => { setBuka(false); setNota(''); };

    /* Waktu dicadangkan pada saat pemilih DIBUKA, bukan sekali semasa
       komponen lahir — senarai waktu berubah setiap kali pengguna
       menambah satu, dan cadangan yang dikira sekali sahaja akan
       mengulangi waktu yang dia baru sahaja masukkan. */
    const bukaPemilih = () => { setJam(cadangWaktuBaharu(sudahAda.map(jamKeMinit))); setNota(''); setBuka(true); };

    /* Tiada lagi pelarasan di sini. Pemilih tidak menawarkan minit di luar
       roda TikTok, jadi mesej "Dilaraskan ke 08:05…" yang dahulu muncul
       selepas pengguna menaip sudah tidak boleh berlaku — dan mesej itu
       memang tidak sepatutnya wujud: ia menegur pengguna atas peraturan
       yang tidak pernah ditunjukkan kepadanya sebelum dia menaip. */
    const simpan = () => {
        if (sudahAda.includes(jam)) {
            setNota(T('workspace.schedule.timeExists', `${jam} sudah ada dalam jadual ini.`, { jam }));
            return;
        }
        onTambah(jam);
        tutup();
    };

    if (!buka) {
        return (
            <button
                type="button" className="s-kal__tambah"
                style={{ width: 'auto', padding: '5px 10px', marginTop: 0 }}
                onClick={bukaPemilih}
            >
                <Plus size={12} strokeWidth={2} aria-hidden="true" />
                {T('workspace.schedule.addTime', 'Tambah waktu')}
            </button>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* `flexWrap` wajib — lajur tetapan ini sempit, dan tanpa balutan
                butang terakhir terpotong di tepi kad. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <PilihWaktuV39
                    value={jam} label={T('workspace.schedule.newTimeAria', 'Waktu baharu')} autoFocus
                    onChange={(v) => { setJam(v); setNota(''); }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') simpan();
                        if (e.key === 'Escape') tutup();
                    }}
                />
                <Btn size="sm" onClick={simpan}>{T('workspace.schedule.addBtn', 'Tambah')}</Btn>
                <Btn size="sm" variant="quiet" onClick={tutup}>{T('workspace.schedule.closeBtn', 'Tutup')}</Btn>
            </div>
            {nota && <span style={{ fontSize: 11, color: 'var(--s-ink-3)' }}>{nota}</span>}
        </div>
    );
}

function Suis({ on, onChange, disabled, label }) {
    return (
        <button
            type="button" role="switch" aria-checked={!!on} aria-label={label}
            disabled={disabled} onClick={onChange}
            className={cx('s-switch', on && 's-switch--on')}
        >
            <span className="s-switch__dot" />
        </button>
    );
}

/* Satu baris tetapan: label kiri dengan lebar tetap, kawalan di kanan.
   Lebar label yang SAMA untuk semua baris — itulah yang menjadikan
   senarai Cal senang dibaca menegak. */
function Baris({ label, nota, children, last }) {
    return (
        <div style={{
            display: 'grid', gridTemplateColumns: '96px minmax(0,1fr)',
            gap: 12, alignItems: 'start', padding: '11px 14px',
            borderBottom: last ? 'none' : '1px solid var(--s-line)',
        }}>
            <span style={{ fontSize: 13, color: 'var(--s-ink-2)', paddingTop: 6 }}>{label}</span>
            <div style={{ minWidth: 0 }}>
                {children}
                {nota && <p className="s-hint">{nota}</p>}
            </div>
        </div>
    );
}

function Pil({ on, onClick, children, lebar }) {
    return (
        <button
            type="button"
            aria-pressed={on}
            onClick={onClick}
            className={cx('s-pill', on && 's-pill--on')}
            style={lebar ? { minWidth: lebar } : undefined}
        >
            {children}
        </button>
    );
}


/* ---------- Modal ---------------------------------------------------- */

export default function ScheduleTemplateModalV39({
    senarai, setSenarai, onClose,
    /* Berapa HARI (merentas semua akaun kalendar) yang memakai setiap
       template: { [idTemplate]: bilangan }. Ia dihantar masuk kerana
       susunan kalendar bukan milik modal ini — tetapi tanpa angka itu,
       pengguna memadam template tanpa tahu apa yang akan runtuh. */
    guna = {},
    /* Dipanggil SELEPAS template dipadam supaya pemanggil MENGOSONGKAN
       hari yang memakainya — penunjuk template hari dibuang dan isian hari
       itu dikosongkan sekali (keputusan user 2026-08-26; reka bentuk asal
       "kekalkan waktu sebagai waktu tersendiri" DITOLAK sebagai leceh).
       Tanpa wayar ini, hari tersebut menyimpan videonya tetapi tidak dapat
       memaparkannya — slot hantu yang kelihatan seperti kerja yang hilang. */
    onTemplatePadam,
    /* Fungsi terjemahan. Dihantar oleh halaman v3.9 sahaja — lihat nota
       pada `ambilTeks` di atas. Tanpa ia, modal kekal Melayu. */
    t,
}) {
    const T = ambilTeks(t);

    const [aktifId, setAktifId] = useState(senarai[0]?.id || null);
    const [sahPadam, setSahPadam] = useState(false);

    /* ══ SUSUN SEMULA SENARAI TEMPLATE (tarik-lepas / anak panah) ═══════
       Susunan template ialah susunan array `senarai` itu sendiri — pemanggil
       menyimpannya verbatim ke browser DAN server, jadi menyusun semula array
       terus tersimpan tanpa medan `order` berasingan (beza dengan senarai
       agent yang datang daripada query Firestore yang menyusun sendiri).

       Ia operasi SENARAI seperti Duplicate/Delete/New — hasilnya kelihatan
       serta-merta dan TIDAK melalui draf. Menyusun tidak menyentuh `aktifId`
       mahupun draf template yang sedang dibuka (draf dikunci pada id, bukan
       kedudukan), jadi kerja yang belum disimpan kekal selamat. */
    const [tarikId, setTarikId] = useState(null);
    const [sasar, setSasar] = useState(null); // { id, bawah }
    const adaTetikus = useMemo(() => bolehTarikTetikus(), []);
    const bolehSusun = senarai.length > 1;

    const naikTurun = (dari, ke) => {
        if (ke < 0 || ke >= senarai.length) return;
        setSenarai(alihItem(senarai, dari, ke));
    };

    const lepasPada = (idSasar, bawah) => {
        const ids = senarai.map((j) => j.id);
        const dari = ids.indexOf(tarikId);
        const atas = ids.indexOf(idSasar);
        setTarikId(null);
        setSasar(null);
        if (dari < 0 || atas < 0) return;
        setSenarai(sisipItem(senarai, dari, bawah ? atas + 1 : atas));
    };

    /* Pilihan hari/bulan DIBUANG bersama kalendar pratonton — pratonton
       kini bacaan lurus hari demi hari, jadi tiada apa yang perlu dipilih. */

    /* ══ DRAF + SAVE (arahan user 2026-08-14) ══════════════════════════
       Dahulu setiap tekanan terus menulis ke senarai sebenar, dan satu-
       satunya butang ialah "Done". Ia memang berfungsi, tetapi tidak ada
       apa-apa di skrin yang memberitahu perubahan sudah disimpan — jadi
       pengguna menekan Done sambil tertanya-tanya sama ada templatenya
       tersimpan atau tidak. Aduan user: "takut user ni keliru adakah
       template tu dah simpan ke tak."

       Sekarang: pengeditan medan (nama, set, interleave, waktu) hidup
       dalam DRAF; hanya "Save" menulisnya ke senarai sebenar.

       ⛔ Duplicate, Delete dan New template KEKAL serta-merta. Ia operasi
          SENARAI, bukan medan — hasilnya kelihatan sendiri (baris baharu
          muncul, baris hilang). Menariknya ke dalam draf akan menimbulkan
          soalan yang tiada jawapan baik: kalau anda padam template
          kemudian tekan Batal, patutkah ia hidup semula? */
    const tersimpan = senarai.find((j) => j.id === aktifId) || null;
    const [draf, setDraf] = useState(tersimpan);

    /* Draf dimuat semula HANYA bila template lain dipilih. Sengaja tidak
       mengikut `senarai` — kalau tidak, setiap tulisan luar (cth kalendar
       membuang slot) akan memadam apa yang pengguna sedang taip. */
    useEffect(() => {
        setDraf(senarai.find((j) => j.id === aktifId) || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktifId]);

    const aktif = draf;
    const adaUbah = !!draf && !!tersimpan && JSON.stringify(draf) !== JSON.stringify(tersimpan);

    /* Tindakan yang tergantung menunggu keputusan pengguna tentang
       perubahan yang belum disimpan. Disimpan sebagai fungsi supaya satu
       baris amaran boleh melayan semua laluan: tukar template, tambah,
       salin, dan tutup modal. */
    const [amaran, setAmaran] = useState(null);

    const simpan = () => {
        if (!draf) return;
        setSenarai((prev) => prev.map((j) => (j.id === draf.id ? draf : j)));
    };

    /* Pagar sekali guna: kalau ada perubahan belum disimpan, tindakan
       ditahan dan baris amaran keluar di jalur kaki. */
    const pagar = (tindakan) => {
        if (adaUbah) { setAmaran(() => tindakan); return; }
        tindakan();
    };

    const ubah = (patch) => {
        setDraf((prev) => {
            if (!prev) return prev;
            const baru = { ...prev, ...patch };
            /* Nama automatik terus mengikut tetapan SELAGI pengguna belum
               menamakannya sendiri. Sebaik dia menaip nama, kita berhenti
               menulis ganti — itu namanya, bukan nama kita. */
            if (!baru.namaSendiri) baru.nama = namaAuto(baru);
            return baru;
        });
    };

    /* Menukar template semasa membatalkan pengesahan padam yang tergantung —
       kalau tidak, "Delete" berikutnya merujuk template yang pengguna sudah
       tidak lagi melihat. */
    const pilihJadual = (id) => pagar(() => { setSahPadam(false); setAktifId(id); });

    const cubaTutup = () => pagar(onClose);

    /* Esc melalui ref: pendengar dipasang SEKALI, tetapi keputusan sama ada
       boleh terus tutup bergantung pada keadaan draf yang bertukar setiap
       tekanan kekunci. Tanpa ref, pendengar memegang keadaan lama dan Esc
       akan membuang kerja pengguna secara senyap. */
    const cubaTutupRef = useRef(null);
    cubaTutupRef.current = cubaTutup;
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') cubaTutupRef.current?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    /* Kedua-dua tindakan ini menukar template yang sedang dibuka, jadi ia
       melalui pagar yang sama seperti menekan baris lain dalam senarai —
       kalau tidak, draf yang belum disimpan lenyap tanpa sebarang amaran. */
    const tambah = () => pagar(() => {
        const baru = jadualBaharu();
        setSenarai((prev) => [...prev, baru]);
        setAktifId(baru.id);
    });

    /* Menyalin jadual, kemudian tukar setnya sahaja — inilah cara
       pengguna menyiapkan browser 2 dan 3 dalam beberapa saat, bukan
       mengisi borang yang sama tiga kali. */
    const salin = () => pagar(() => {
        if (!aktif) return;
        const baru = salinJadual(aktif);
        setSenarai((prev) => [...prev, baru]);
        setAktifId(baru.id);
    });

    const padam = () => {
        const jadualDipadam = aktif;
        if (!jadualDipadam) return;

        const baki = senarai.filter((j) => j.id !== jadualDipadam.id);
        setSenarai(baki);
        setAktifId(baki[0]?.id || null);
        setSahPadam(false);

        /* Pemanggil MENGOSONGKAN hari yang memakai template ini (penunjuk
           + isian hari itu) — lihat nota pada prop `onTemplatePadam` di
           atas. Pembersihan berlaku di luar sini kerana susunan kalendar
           bukan milik modal ini. Amaran di kaki modal sudah memberitahu
           bilangan hari yang akan dikosongkan sebelum pengguna mengesahkan. */
        onTemplatePadam?.(jadualDipadam.id);
    };

    const gunaAktif = aktifId ? (guna[aktifId] || 0) : 0;

    const toggleSet = (s) => ubah({
        sets: aktif.sets.includes(s) ? aktif.sets.filter((x) => x !== s) : [...aktif.sets, s].sort(),
    });

    const toggleSlot = (waktu) => ubah({
        slot: aktif.slot.includes(waktu) ? aktif.slot.filter((x) => x !== waktu) : [...aktif.slot, waktu].sort(),
    });

    /* ⭐ "Isi automatik" MENGGANTI senarai, bukan menambah (arahan user
       2026-08-26). Ia DAHULU menambah (union) supaya waktu yang ditaip
       sendiri tidak lenyap — tetapi setiap waktu selang 1 jam memang wujud
       dalam grid selang 30 minit, jadi "pilih 1 jam → tekan Isi" ke atas
       senarai 30 minit TIDAK mengubah apa-apa, tanpa sebarang mesej. Dua
       aduan sebenar lahir daripadanya (24 + 26 Ogos 2026): pengguna yakin
       templatenya selang 1 jam sedangkan robot post setiap 30 minit — dan
       robot itu BETUL, template yang tersilap isi. Arah songsangnya sama
       buruk: tekan Isi semasa alat memapar lalai 30 minit menambah waktu
       :30 ke dalam template 1 jam yang sudah betul.
       Harga yang diterima SECARA SEDAR: waktu yang ditaip tangan turut
       diganti apabila Isi ditekan — pengguna boleh menambahnya semula
       melalui "Tambah waktu". ⛔ Jangan kembalikan union tanpa membaca
       nota ini. ⚠️ Pasangan wajib: fix ingat-pilihan (KUNCI_ISI di atas,
       LIVE sejak 2026-08-25) MESTI kekal bersama perubahan ini — tanpa
       ia, alat sentiasa lahir memapar "30 minit", dan satu tekanan Isi
       yang cuai akan MENGGANTI template 1 jam dengan 30 minit, bukan
       sekadar menambah. */
    const isiWaktu = (mula, henti, selang) => ubah({
        slot: isiSelang(mula, henti, selang),
    });

    /* ⭐ Pratonton produk contoh DIBUANG (arahan user 2026-08-14). Ia dibina
       daripada produk REKAAN, sedangkan kalendar di belakang modal ini sudah
       menunjukkan susunan sebenar dengan produk sebenar. Dua paparan yang
       menjawab soalan sama, satu daripadanya palsu. */
    const banyakSet = !!aktif && aktif.sets.length > 1;

    /* Dipaparkan melalui portal ke <body> — lihat nota pada `.s-sched-panel`
       dalam ui.css. Kelas `s-root` DIGABUNG di sini kerana semua token
       --s-* ditakrif padanya, dan portal membawa modal keluar daripada
       pokok yang memilikinya; tanpa ia, seluruh modal kehilangan warna.
       Latar overlay ditulis terus supaya ia tidak bergantung pada
       kelas mana yang datang dahulu dalam fail CSS. */
    return createPortal(
        <div
            className="s-root s-modal"
            style={{ background: 'rgba(0, 0, 0, .62)' }}
            role="dialog" aria-modal="true" aria-label={T('workspace.schedule.modalAria', 'Schedule template')}
            onClick={cubaTutup}
        >
            <div
                className="s-modal__panel s-sched-panel"
                onClick={(e) => e.stopPropagation()}
            >

                {/* ---- Kepala ---- */}
                <header className="s-modal__head">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="s-cardname">{T('workspace.schedule.title', 'Schedule templates')}</p>
                        <p className="s-dim" style={{ marginTop: 1 }}>
                            {T('workspace.schedule.subtitle', 'Peraturan masa sahaja — produk dipilih semasa Run, tarikh dipilih dalam kalendar.')}
                        </p>
                    </div>
                    <button
                        type="button" onClick={cubaTutup} className="s-modal__x"
                        aria-label={T('workspace.schedule.closeAria', 'Close')}
                        title={T('workspace.schedule.closeTitle', 'Close (Esc)')}
                    >
                        <X size={15} strokeWidth={2} />
                    </button>
                </header>

                <div className="s-sched">

                    {/* ================= KIRI · senarai template =================
                        Senarai menggantikan dropdown. Dropdown menyembunyikan dua
                        perkara yang pengguna perlukan sebelum dia memadam apa-apa:
                        berapa waktu setiap template ada, dan berapa hari yang
                        sedang bergantung padanya. */}
                    <div className="s-sched__set">
                        <p className="s-lbl" style={{ marginBottom: 8 }}>{T('workspace.schedule.listHeading', 'Templates')}</p>

                        {senarai.length === 0 ? (
                            <p className="s-dim" style={{ margin: '0 0 10px', fontSize: 12 }}>
                                {T('workspace.schedule.listEmpty', 'Belum ada template.')}
                            </p>
                        ) : (
                            <div className="s-tpllist">
                                {senarai.map((j, i) => {
                                    const bilWaktu = (j.slot || []).length;
                                    const dipakai = guna[j.id] || 0;
                                    const ditarik = tarikId === j.id;
                                    const tandaJatuh = !!tarikId && tarikId !== j.id && sasar?.id === j.id;
                                    return (
                                        <div
                                            key={j.id}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 2,
                                                borderRadius: 8,
                                                opacity: ditarik ? 0.45 : 1,
                                                /* Garis pendaratan sebagai bayang DALAM, bukan border —
                                                   border menambah tinggi baris, jadi seluruh senarai
                                                   teranjak setiap kali kursor bergerak. */
                                                boxShadow: tandaJatuh
                                                    ? `inset 0 ${sasar.bawah ? -2 : 2}px 0 var(--s-select)`
                                                    : undefined,
                                            }}
                                            draggable={bolehSusun && adaTetikus}
                                            onDragStart={(e) => {
                                                setTarikId(j.id);
                                                e.dataTransfer.effectAllowed = 'move';
                                                /* Sebahagian browser membatalkan tarikan tanpa data. */
                                                try { e.dataTransfer.setData('text/plain', j.id); } catch { /* abaikan */ }
                                            }}
                                            onDragEnd={() => { setTarikId(null); setSasar(null); }}
                                            onDragOver={(e) => {
                                                if (!tarikId || tarikId === j.id) return;
                                                /* preventDefault WAJIB — tanpanya browser menolak
                                                   lepasan dan onDrop tidak pernah dipanggil. */
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                                const r = e.currentTarget.getBoundingClientRect();
                                                const bawah = e.clientY > r.top + r.height / 2;
                                                setSasar((prev) => (prev?.id === j.id && prev.bawah === bawah
                                                    ? prev : { id: j.id, bawah }));
                                            }}
                                            onDrop={(e) => {
                                                if (!tarikId || tarikId === j.id) return;
                                                e.preventDefault();
                                                const r = e.currentTarget.getBoundingClientRect();
                                                lepasPada(j.id, e.clientY > r.top + r.height / 2);
                                            }}
                                        >
                                            {bolehSusun && adaTetikus && (
                                                <span
                                                    title={T('workspace.schedule.dragHandle', 'Drag to rearrange')}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', flex: 'none',
                                                        paddingLeft: 2, color: 'var(--s-ink-3)', cursor: 'grab',
                                                    }}
                                                >
                                                    <GripVertical size={14} strokeWidth={1.9} aria-hidden="true" />
                                                </span>
                                            )}

                                            <button
                                                type="button"
                                                className={'s-tplrow' + (j.id === aktifId ? ' s-tplrow--on' : '')}
                                                onClick={() => pilihJadual(j.id)}
                                                style={{ flex: 1, minWidth: 0, width: 'auto' }}
                                            >
                                                <span className="s-tplrow__nama">{j.nama}</span>
                                                <span className="s-tplrow__meta">
                                                    {bilWaktu
                                                        ? T('workspace.schedule.slotsPerDay', `${bilWaktu} slots/day`, { n: bilWaktu })
                                                        : T('workspace.schedule.noTimesSet', 'No times set')}
                                                    {dipakai > 0 && ` · ${T(dipakai === 1 ? 'workspace.schedule.usedByDay' : 'workspace.schedule.usedByDays', `Used by ${dipakai} ${dipakai === 1 ? 'day' : 'days'}`, { n: dipakai })}`}
                                                </span>
                                            </button>

                                            {/* Anak panah HANYA pada peranti sentuh (arahan user
                                                2026-08-19): tarik-lepas HTML5 mati pada satu sentuhan,
                                                jadi telefon perlu laluan lain; komputer sudah cukup
                                                dengan tarik. Kedua-duanya mati pada hujung senarai. */}
                                            {bolehSusun && !adaTetikus && (
                                                <div style={{ display: 'flex', alignItems: 'center', flex: 'none', gap: 2, paddingRight: 2 }}>
                                                    <IconBtn
                                                        label={T('workspace.schedule.moveUp', 'Move up')}
                                                        icon={<ChevronUp size={14} strokeWidth={2} />}
                                                        disabled={i === 0}
                                                        onClick={(e) => { e.stopPropagation(); naikTurun(i, i - 1); }}
                                                        style={i === 0 ? { opacity: 0.35, cursor: 'default' } : undefined}
                                                    />
                                                    <IconBtn
                                                        label={T('workspace.schedule.moveDown', 'Move down')}
                                                        icon={<ChevronDown size={14} strokeWidth={2} />}
                                                        disabled={i === senarai.length - 1}
                                                        onClick={(e) => { e.stopPropagation(); naikTurun(i, i + 1); }}
                                                        style={i === senarai.length - 1 ? { opacity: 0.35, cursor: 'default' } : undefined}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <Btn
                            variant="quiet" size="sm" onClick={tambah}
                            icon={<Plus size={14} strokeWidth={2} />}
                        >
                            {T('workspace.schedule.newTemplate', 'New template')}
                        </Btn>
                    </div>

                    {/* ================= KANAN · tetapan template ================= */}
                    <div className="s-sched__cal">
                        {!aktif ? (
                            <EmptyState
                                title={T('workspace.schedule.noneSelected', 'No template selected')}
                                action={(
                                    <Btn size="sm" onClick={tambah} icon={<Plus size={14} strokeWidth={2} />}>
                                        {T('workspace.schedule.newTemplate', 'New template')}
                                    </Btn>
                                )}
                            >
                                {T('workspace.schedule.noneSelectedBody', 'Buat satu template untuk menetapkan waktu post anda.')}
                            </EmptyState>
                        ) : (
                            <div style={{
                                border: '1px solid var(--s-line)', borderRadius: 'var(--s-r-lg)',
                                overflow: 'hidden',
                            }}>
                                <Baris label={T('workspace.schedule.rowName', 'Name')}>
                                    <input
                                        className="s-input"
                                        type="text"
                                        value={aktif.nama}
                                        onChange={(e) => ubah({ nama: e.target.value, namaSendiri: true })}
                                        aria-label={T('workspace.schedule.nameAria', 'Template name')}
                                        style={{ width: '100%', maxWidth: 320 }}
                                    />
                                </Baris>

                                <Baris
                                    label={T('workspace.schedule.rowSets', 'Sets')}
                                    nota={T('workspace.schedule.setsHint', 'Buat satu template bagi setiap set kalau set berlainan pergi ke browser berlainan.')}
                                >
                                    <div style={{ display: 'flex', gap: 8 }} role="group" aria-label={T('workspace.schedule.rowSets', 'Sets')}>
                                        {['set1', 'set2', 'set3'].map((s, i) => (
                                            <Pil key={s} on={aktif.sets.includes(s)} onClick={() => toggleSet(s)}>
                                                S{i + 1}
                                            </Pil>
                                        ))}
                                    </div>
                                </Baris>

                                {/* Dipindahkan MASUK ke dalam kad tetapan. Dahulu ia
                                    terapung di atas kad sebagai satu-satunya tetapan
                                    istimewa — tetapi ia tetapan template seperti yang
                                    lain, dan berdiri di luar hanya membuatkan pengguna
                                    tertanya-tanya kenapa ia berbeza. */}
                                <Baris
                                    label={T('workspace.schedule.rowInterleave', 'Interleave sets')}
                                    nota={banyakSet
                                        ? T('workspace.schedule.interleaveHintOn', 'Hantar set 1 bagi semua produk dahulu, kemudian set 2. Kalau dimatikan, satu produk dihabiskan dahulu sebelum produk berikutnya.')
                                        : T('workspace.schedule.interleaveHintOff', 'Tersedia apabila lebih daripada satu set dipilih.')}
                                >
                                    <Suis
                                        label={T('workspace.schedule.rowInterleave', 'Interleave sets')}
                                        disabled={!banyakSet}
                                        on={aktif.turutan === 'set'}
                                        onChange={() => ubah({ turutan: aktif.turutan === 'set' ? 'kad' : 'set' })}
                                    />
                                </Baris>

                                {/* ⭐ SATU senarai waktu — tiada lagi suis "Selang / Slot tetap"
                                    (arahan user 2026-08-14). Suis itu memaksa pengguna memilih
                                    antara dua model fikiran untuk satu hasil yang sama.

                                    Kelajuan dijaga oleh "Isi automatik": ia MENGISI senarai ini,
                                    bukan menjadi mod berasingan. Selepas diisi setiap waktu boleh
                                    ditanggalkan atau ditambah satu per satu — tiada apa yang
                                    kekal terikat pada peraturan itu. */}
                                <Baris
                                    label={T('workspace.schedule.rowSendTimes', 'Send times')}
                                    nota={T(
                                        'workspace.schedule.sendTimesHint',
                                        `Waktu yang ditanda akan diguna SETIAP hari. Video yang melebihi bilangan ini disambung pada hari berikutnya. Setiap ${LANGKAH_MINIT} minit sahaja — had TikTok.`,
                                        { minit: LANGKAH_MINIT },
                                    )}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <IsiAutomatik T={T} onIsi={isiWaktu} onKosong={() => ubah({ slot: [] })} adaIsi={aktif.slot.length > 0} />

                                        {/* Template tanpa waktu BUKAN ralat — ia keadaan
                                            sah semasa pengguna sedang membina. Tetapi hari
                                            yang memakainya langsung tiada slot, jadi ia
                                            mesti berkata begitu di sini dan bukan hanya
                                            kelihatan sebagai kalendar kosong nanti. */}
                                        {aktif.slot.length === 0 ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <Badge tone="warn">{T('workspace.schedule.noTimesSet', 'No times set')}</Badge>
                                                <span className="s-dim" style={{ fontSize: 12 }}>
                                                    {T('workspace.schedule.noTimesHint', 'Hari yang memakai template ini tiada slot langsung.')}
                                                </span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                                {/* Pemboleh ubah dinamakan `waktu`, bukan `t` — `t` kini
                                                    ialah fungsi terjemahan yang masuk sebagai prop, dan
                                                    nama yang sama di dalam sini akan menutupnya. */}
                                                {[...aktif.slot].sort().map((waktu) => (
                                                    <span key={waktu} className="s-chipwaktu">
                                                        {waktu}
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleSlot(waktu)}
                                                            title={T('workspace.schedule.removeTime', `Buang ${waktu}`, { t: waktu })}
                                                            aria-label={T('workspace.schedule.removeTime', `Buang ${waktu}`, { t: waktu })}
                                                        >
                                                            <X size={11} strokeWidth={2.4} />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <TambahWaktuTemplate T={T} sudahAda={aktif.slot} onTambah={(waktu) => toggleSlot(waktu)} />
                                    </div>
                                </Baris>

                                {/* ⭐ Baris "Mula" (Hari ini / Esok) DIBUANG (arahan user
                                    2026-08-14). Template tidak memilih hari — hari
                                    ditentukan oleh lajur mana pengguna menjatuhkan kad
                                    dalam kalendar. Medan itu hanya dibaca oleh `letakMasa`,
                                    yang tinggal dipakai oleh panel prototaip sahaja. */}

                                {/* Kaki kad — zon waktu, sama kedudukan dengan rujukan.
                                    Ia bukan hiasan: jam yang ditulis ke TikTok mesti
                                    difahami dalam zon yang sama oleh pengguna. */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    gap: 10, padding: '11px 14px', borderTop: '1px solid var(--s-line)',
                                }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 7,
                                        fontSize: 12.5, color: 'var(--s-ink-2)',
                                    }}>
                                        <Globe size={14} strokeWidth={1.9} />
                                        Asia/Kuala_Lumpur
                                    </span>
                                    <span className="s-dim">{T('workspace.schedule.dateAtRun', 'Tarikh dikira semasa Run')}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ---- Kaki: tindakan template ----
                    Butang BERLABEL, bukan ikon bogel. Padam ialah tindakan yang
                    paling merosakkan di skrin ini, dan ia dahulunya sebuah ikon
                    tong sampah tanpa pengesahan — satu klik tersasar sudah cukup
                    untuk melenyapkan template beserta slot setiap hari yang
                    memakainya. */}
                <footer className="s-modal__foot">
                    {/* Amaran perubahan belum disimpan. Ia MENAHAN tindakan
                        yang pengguna cuba buat (tukar template, tambah,
                        salin, tutup) dan bertanya dahulu — sama corak
                        dengan pengesahan Delete di bawah, supaya jalur kaki
                        ini sentiasa berkelakuan sama bila ia bertanya.
                        "Save" meneruskan tindakan tadi selepas menyimpan,
                        jadi pengguna tidak perlu menekannya dua kali. */}
                    {amaran ? (
                        <>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--s-ink-2)',
                            }}>
                                <AlertTriangle size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-warn, #d97706)' }} />
                                <span style={{ minWidth: 0 }}>{T('workspace.schedule.unsavedWarn', 'Perubahan pada template ini belum disimpan.')}</span>
                            </span>
                            <Btn
                                variant="quiet" size="sm"
                                onClick={() => { setDraf(tersimpan); const tindakan = amaran; setAmaran(null); tindakan(); }}
                            >
                                {T('workspace.schedule.discardChanges', 'Buang perubahan')}
                            </Btn>
                            <Btn
                                size="sm"
                                onClick={() => { simpan(); const tindakan = amaran; setAmaran(null); tindakan(); }}
                            >
                                {T('workspace.schedule.save', 'Save')}
                            </Btn>
                        </>
                    ) : sahPadam && aktif ? (
                        <>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--s-ink-2)',
                            }}>
                                <AlertTriangle size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-warn, #d97706)' }} />
                                <span style={{ minWidth: 0 }}>
                                    {T('workspace.schedule.deleteConfirm', `Delete “${aktif.nama}”?`, { nama: aktif.nama })}
                                    {/* Kunci tunggal/jamak berasingan — enjin i18n tiada
                                        peraturan jamak, dan "Used by 1 days" dibaca
                                        sebagai cuai (corak sama products.profile.group1). */}
                                    {gunaAktif > 0
                                        ? ` ${T(gunaAktif === 1 ? 'workspace.schedule.deleteUsedNoteOne' : 'workspace.schedule.deleteUsedNote', `Digunakan oleh ${gunaAktif} hari — semua slot pada hari tersebut akan dikosongkan.`, { n: gunaAktif })}`
                                        : ` ${T('workspace.schedule.deleteUnusedNote', 'Template ini tidak digunakan oleh mana-mana hari.')}`}
                                </span>
                            </span>
                            <Btn variant="quiet" size="sm" onClick={() => setSahPadam(false)}>{T('workspace.schedule.cancel', 'Cancel')}</Btn>
                            <Btn size="sm" onClick={padam}>{T('workspace.schedule.delete', 'Delete')}</Btn>
                        </>
                    ) : (
                        <>
                            {aktif && (
                                <>
                                    <Btn variant="quiet" size="sm" onClick={salin} icon={<Copy size={14} strokeWidth={2} />}>
                                        {T('workspace.schedule.duplicate', 'Duplicate')}
                                    </Btn>
                                    <Btn variant="quiet" size="sm" onClick={() => setSahPadam(true)} icon={<Trash2 size={14} strokeWidth={2} />}>
                                        {T('workspace.schedule.delete', 'Delete')}
                                    </Btn>
                                </>
                            )}
                            <span style={{ flex: 1 }} />
                            {/* SATU tindakan utama pada satu-satu masa.
                                Selagi "Save" kelihatan, pengguna tahu ada
                                sesuatu yang belum disimpan — itu jawapan
                                kepada soalan "dah simpan ke belum?" tanpa
                                dia perlu bertanya. Sebaik disimpan, butang
                                bertukar menjadi Done dan tanda "Saved"
                                keluar di sebelahnya. */}
                            {adaUbah ? (
                                <>
                                    <Btn variant="quiet" size="sm" onClick={() => setDraf(tersimpan)}>
                                        {T('workspace.schedule.cancelDraft', 'Batal')}
                                    </Btn>
                                    <Btn size="sm" onClick={simpan}>{T('workspace.schedule.save', 'Save')}</Btn>
                                </>
                            ) : (
                                <>
                                    {aktif && (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5,
                                            fontSize: 12, color: 'var(--s-ink-3)', marginRight: 2,
                                        }}>
                                            <Check size={13} strokeWidth={2.2} style={{ color: 'var(--s-ok, #16a34a)' }} />
                                            {T('workspace.schedule.saved', 'Saved')}
                                        </span>
                                    )}
                                    <Btn size="sm" onClick={cubaTutup}>{T('workspace.schedule.done', 'Done')}</Btn>
                                </>
                            )}
                        </>
                    )}
                </footer>
            </div>
        </div>,
        document.body,
    );
}
